import { useEffect, useMemo, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import type { ReactNode } from 'react'
import { api, type BadgeBoard } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Page, Progress } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { Trace, TraceRegion } from '../lib/trace'
import { Mascot } from '../components/Mascot'
import { sfx } from '../lib/sound'
import { useViewport, NARROW } from '../lib/screen'

/**
 * The trophy room.
 *
 * Until 4 Oct 2026 this was a list of cards. Sam asked for something to
 * walk into: a room with shelves, where what a child has found stands as a
 * shiny trophy and what they are working towards stands beside it, dim, with
 * how far along they are. Hidden ones are wrapped presents.
 *
 * **Nothing here can fall.** A trophy, once on the shelf, stays (D-048). The
 * progress rings only fill. There is no score and no comparison: a brother's
 * new trophy is mentioned, warmly, never ranked against.
 *
 * **What is on the shelves is whatever the badge table holds.** The room does
 * not decide what deserves a trophy; badges.py does. Since 5 Oct 2026 every
 * badge rewards learning (D-064). The six that rewarded streaks, minutes and
 * luck are retired: no one can earn them, and a child who has one keeps it.
 */

type Item =
  | { kind: 'found'; code: string; name: string; emoji: string; description: string; awarded_at: string }
  | { kind: 'towards'; code: string; name: string; emoji: string; description: string; now: number; target: number; unit: string }
  | { kind: 'hidden'; code: string }

export function Badges({
  learnerId, onHome, onGarden, on3D,
}: { learnerId: string; onHome: () => void; onGarden: () => void; on3D?: () => void }) {
  const [board, setBoard] = useState<BadgeBoard | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [open, setOpen] = useState<Item | null>(null)
  const { width } = useViewport()
  const perShelf = width < NARROW ? 3 : 4

  const load = () => {
    setError(null)
    api.badges(learnerId).then(setBoard).catch(setError)
  }
  useEffect(load, [learnerId])

  const items: Item[] = useMemo(() => {
    if (!board) return []
    return [
      ...board.found.map((b) => ({ kind: 'found' as const, ...b })),
      ...board.working_towards.map((b) => ({ kind: 'towards' as const, ...b })),
      ...Array.from({ length: board.hidden_count }, (_, i) => ({ kind: 'hidden' as const, code: `hidden-${i}` })),
    ]
  }, [board])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!board) return <Loading />

  const shelves: Item[][] = []
  for (let i = 0; i < Math.max(items.length, 1); i += perShelf) shelves.push(items.slice(i, i + perShelf))
  const found = board.found.length

  return (
    <Page width={640}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ margin: '4px 0 2px', fontSize: 30, color: theme.text }}>
          {board.display_name}&apos;s trophy room
        </h1>
        {/* The 3D room where trophies can be moved and placed (8 Oct 2026).
            This flat room stays, for anyone who prefers it. */}
        {on3D ? <Button tone="child" onClick={on3D}>🏠 Arrange in 3D</Button> : null}
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 16, color: theme.muted }}>
        <Trace id="calc.counted from T7 rows">{board.counters.words_met} words met</Trace>
        {' · '}
        <Trace id="calc.counted from T7.grown_at">{board.counters.words_mastered} fully grown</Trace>
        {' · '}{found} {found === 1 ? 'trophy' : 'trophies'}
      </p>

      {/* ------------------------------------------------------- the room */}
      <div style={{
        position: 'relative', borderRadius: 22, overflow: 'hidden',
        border: `3px solid ${theme.borderWarm}`,
        // The wall only. The floor is its own strip below the shelves, so it
        // sits in the right place however many shelves a child's trophies
        // need — a fixed band put the third shelf half on the floor.
        background: 'linear-gradient(180deg, #FFF4E2 0%, #FDE9CF 100%)',
        padding: '18px 14px 0',
        boxShadow: '0 6px 18px rgba(80,50,20,0.12)',
      }}>
        <RoomDecor />

        <div style={{ position: 'relative', paddingTop: 66 }}>
          {shelves.map((shelf, i) => (
            <Shelf key={i}>
              {shelf.map((item) => (
                <TrophySpot key={item.code} item={item} onOpen={() => { sfx.tap(); setOpen(item) }} />
              ))}
            </Shelf>
          ))}
        </div>

        {/* the floor: wooden boards, a rug, and Sprout */}
        <div style={{
          position: 'relative', height: 120, margin: '6px -14px 0',
          background: 'repeating-linear-gradient(90deg, #DDB98A 0 58px, #D2AD7C 58px 60px)',
          borderTop: '4px solid #C69C68',
        }}>
          <div style={{
            position: 'absolute', left: '50%', bottom: 14, transform: 'translateX(-50%)',
            width: 230, height: 46, borderRadius: '50%',
            background: `repeating-radial-gradient(ellipse at center, ${theme.orangeTint} 0 10px, ${theme.orangeWarm} 10px 20px)`,
            border: `3px solid ${theme.orange}`, opacity: 0.85,
          }} />
          <div style={{ position: 'absolute', left: '50%', bottom: 24, transform: 'translateX(-50%)' }}>
            <Mascot size={86} mood={found ? 'cheer' : 'happy'} label="Sprout" />
          </div>
        </div>
      </div>

      {/* ----------------------------------------------- what was tapped */}
      {open ? <Details item={open} onClose={() => setOpen(null)} /> : (
        <p style={{ textAlign: 'center', color: theme.faint, fontSize: 14, margin: '12px 0 0' }}>
          Tap a trophy to see what it is for.
        </p>
      )}

      {found === 0 ? (
        <p style={{ textAlign: 'center', color: theme.text2, fontSize: 16, margin: '14px 0 0' }}>
          The shelves are waiting. Finish a session and the first trophy arrives.
        </p>
      ) : null}

      {board.sibling_news.length ? (
        <p style={{
          marginTop: 18, padding: '12px 14px', background: theme.blueFaint,
          borderRadius: radius.md, fontSize: 15, color: theme.text2, lineHeight: 1.5,
        }}>
          {board.sibling_news[0]!.display_name} found{' '}
          <Trace id="T18.name"><strong>{board.sibling_news[0]!.name}</strong></Trace>{' '}
          {board.sibling_news[0]!.emoji}. Nice one.
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
        <Button tone="child" onClick={onGarden}>See my garden</Button>
        <ExitButton onExit={onHome} />
      </div>
    </Page>
  )
}

/** A window with sky, a hanging garland of leaves, and a picture frame. */
function RoomDecor() {
  return (
    <>
      {/* garland */}
      <svg viewBox="0 0 600 40" preserveAspectRatio="none" aria-hidden="true"
           style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 40 }}>
        <path d="M0 6 Q150 34 300 10 T600 8" stroke="#6E9E45" strokeWidth="3" fill="none" />
        {Array.from({ length: 13 }, (_, i) => {
          const x = 20 + i * 46
          const y = 14 + Math.sin(i * 0.9) * 8
          return (
            <ellipse key={i} cx={x} cy={y} rx="9" ry="5" fill={i % 3 === 0 ? '#F4B942' : '#7CC157'}
                     transform={`rotate(${i % 2 ? 30 : -30} ${x} ${y})`} />
          )
        })}
      </svg>
      {/* window */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 26, right: 18, width: 74, height: 64,
        borderRadius: 10, border: '5px solid #FFFFFF',
        background: 'linear-gradient(180deg, #9ED8F5 0%, #D6F0FB 100%)',
        boxShadow: '0 2px 0 rgba(0,0,0,0.06)', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 8, left: 10, width: 18, height: 18, borderRadius: 99, background: '#FFD166' }} />
        <div style={{ position: 'absolute', bottom: 10, right: 6, width: 34, height: 12, borderRadius: 99, background: '#fff' }} />
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 4, background: '#fff' }} />
      </div>
      {/* a framed leaf picture */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 30, left: 20, width: 48, height: 56, borderRadius: 6,
        border: '4px solid #C99A63', background: theme.greenPale,
        display: 'grid', placeItems: 'center', fontSize: 24,
      }}>🌿</div>
    </>
  )
}

/** One wooden shelf, with the trophies standing on it. */
function Shelf({ children }: { children: ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-evenly', alignItems: 'flex-end',
        gap: 6, minHeight: 104, padding: '0 6px',
      }}>{children}</div>
      <div style={{
        height: 14, borderRadius: 4,
        background: 'linear-gradient(180deg, #C98E55 0%, #A86F3C 100%)',
        boxShadow: '0 6px 0 rgba(120,80,40,0.18)',
      }} />
    </div>
  )
}

function TrophySpot({ item, onOpen }: { item: Item; onOpen: () => void }) {
  const label = item.kind === 'hidden' ? 'A hidden trophy'
    : item.kind === 'found' ? `${item.name}, found` : `${item.name}, ${item.now} of ${item.target}`
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      style={{
        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        width: 88, minHeight: TAP_MIN, display: 'grid', justifyItems: 'center',
      }}
    >
      {item.kind === 'found' ? (
        <TraceRegion id="T19" note="earned; joined to T18 badge"><Cup emoji={item.emoji} shine /></TraceRegion>
      ) : item.kind === 'towards' ? (
        <TraceRegion id="T18" note="not yet earned">
          <Cup emoji={item.emoji} progress={item.target ? item.now / item.target : 0} />
        </TraceRegion>
      ) : (
        <Gift />
      )}
    </button>
  )
}

let sparkleStyled = false
function ensureSparkle(): void {
  if (sparkleStyled || typeof document === 'undefined') return
  sparkleStyled = true
  const el = document.createElement('style')
  el.textContent = `
    @keyframes ws-twinkle { 0%,100% { opacity: 0; transform: scale(0.6) } 50% { opacity: 1; transform: scale(1) } }
    .ws-twinkle { animation: ws-twinkle 2.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
    @media (prefers-reduced-motion: reduce) { .ws-twinkle { animation: none; opacity: 0.8 } }
  `
  document.head.appendChild(el)
}

/**
 * A trophy cup. Gold and twinkling once found; pale, with a ring that fills,
 * while being worked towards. The badge's own picture sits on its front.
 */
function Cup({ emoji, shine = false, progress }: { emoji: string; shine?: boolean; progress?: number }) {
  ensureSparkle()
  const gold = shine
  const ring = progress === undefined ? null : Math.max(0, Math.min(1, progress))
  const circumference = 2 * Math.PI * 17
  return (
    <div style={{ position: 'relative', width: 80, height: 96 }}>
      <svg viewBox="0 0 80 96" width="80" height="96" aria-hidden="true">
        <defs>
          <linearGradient id="ws-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#FFE38A" />
            <stop offset="0.5" stopColor="#F5B935" />
            <stop offset="1" stopColor="#D08A16" />
          </linearGradient>
          <linearGradient id="ws-pale" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ECE7DE" />
            <stop offset="1" stopColor="#D8D1C4" />
          </linearGradient>
        </defs>
        {/* handles */}
        <path d="M14 18 C2 18 2 40 18 42" stroke={gold ? '#D9981E' : '#CFC7B8'} strokeWidth="5" fill="none" />
        <path d="M66 18 C78 18 78 40 62 42" stroke={gold ? '#D9981E' : '#CFC7B8'} strokeWidth="5" fill="none" />
        {/* bowl */}
        <path d="M12 10 H68 V26 C68 46 54 58 40 58 C26 58 12 46 12 26 Z"
              fill={gold ? 'url(#ws-gold)' : 'url(#ws-pale)'}
              stroke={gold ? '#B97510' : '#C2B9A8'} strokeWidth="2" />
        {gold ? <path d="M20 14 C20 30 24 40 30 46" stroke="#FFF6CF" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.8" /> : null}
        {/* stem and base */}
        <rect x="35" y="57" width="10" height="13" fill={gold ? '#E2A22A' : '#D3CBBC'} />
        <rect x="24" y="69" width="32" height="8" rx="3" fill={gold ? '#C98A1C' : '#C8BFAE'} />
        <rect x="18" y="77" width="44" height="12" rx="3" fill={gold ? '#7A4E1F' : '#B7AD9B'} />
        {/* progress ring around the medallion */}
        {ring !== null ? (
          <circle cx="40" cy="30" r="17" fill="none" stroke={theme.green} strokeWidth="4"
                  strokeDasharray={`${circumference * ring} ${circumference}`}
                  strokeLinecap="round" transform="rotate(-90 40 30)" />
        ) : null}
        {gold ? (
          <g fill="#FFFFFF">
            <path className="ws-twinkle" d="M70 4 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2z" />
            <path className="ws-twinkle" style={{ animationDelay: '1.1s' }} d="M8 52 l1.5 4 4 1.5 -4 1.5 -1.5 4 -1.5 -4 -4 -1.5 4 -1.5z" />
          </g>
        ) : null}
      </svg>
      <span style={{
        position: 'absolute', left: 0, right: 0, top: 18, textAlign: 'center',
        fontSize: 24, lineHeight: 1, filter: gold ? 'none' : 'grayscale(1)',
        opacity: gold ? 1 : 0.55,
      }}>{emoji}</span>
    </div>
  )
}

/** A wrapped present: a trophy nobody has found yet. */
function Gift() {
  return (
    <div style={{ position: 'relative', width: 70, height: 70, marginBottom: 2 }} aria-hidden="true">
      <div style={{
        position: 'absolute', bottom: 0, left: 5, right: 5, height: 46, borderRadius: 6,
        background: `linear-gradient(135deg, ${theme.blueTint}, ${theme.bluePale})`,
        border: `2px solid ${theme.blue}`,
      }} />
      <div style={{
        position: 'absolute', bottom: 44, left: 0, right: 0, height: 14, borderRadius: 5,
        background: theme.blueTint, border: `2px solid ${theme.blue}`,
      }} />
      <div style={{ position: 'absolute', bottom: 0, top: 10, left: 30, width: 10, background: theme.orange }} />
      <div style={{
        position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center',
        fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 20, color: theme.blueDark,
      }}>?</div>
      <div style={{ position: 'absolute', top: 0, left: 24, fontSize: 18 }}>🎀</div>
    </div>
  )
}

function Details({ item, onClose }: { item: Item; onClose: () => void }) {
  return (
    <div role="status" style={{
      marginTop: 14, background: theme.card, border: `1px solid ${theme.borderWarm}`,
      borderRadius: radius.xl, padding: 16, display: 'flex', gap: 14, alignItems: 'flex-start',
    }}>
      <div style={{ fontSize: 34, lineHeight: 1 }}>
        {item.kind === 'hidden' ? '🎁' : <Trace id="T18.emoji">{item.emoji}</Trace>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.kind === 'hidden' ? (
          <>
            <h3 style={{ margin: '0 0 4px', fontSize: 20, color: theme.text }}>Still a surprise</h3>
            <p style={{ margin: 0, fontSize: 15, color: theme.text2, lineHeight: 1.5 }}>
              Some trophies come from what you do. Some just turn up.
            </p>
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 4px', fontSize: 20, color: theme.text }}>
              <Trace id="T18.name">{item.name}</Trace>
            </h3>
            <p style={{ margin: '0 0 8px', fontSize: 15, color: theme.text2, lineHeight: 1.5 }}>
              <Trace id="T18.description">{item.description}</Trace>
            </p>
            {item.kind === 'found' ? (
              <p style={{ margin: 0, fontSize: 14, color: theme.greenDark, fontWeight: 700 }}>
                On your shelf since{' '}
                <Trace id="T19.awarded_at">{new Date(item.awarded_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</Trace>
              </p>
            ) : (
              <>
                <div style={{ fontSize: 14, color: theme.muted, marginBottom: 6 }}>
                  <Trace id="calc.counted from T14 trials and T12 sessions against T18 criterion_json">
                    {item.now} of {item.target} {item.unit}
                  </Trace>
                </div>
                <Progress done={item.now} total={item.target} />
              </>
            )}
          </>
        )}
      </div>
      <button type="button" onClick={onClose} aria-label="Close" style={{
        background: 'transparent', border: 'none', fontSize: 22, color: theme.faint,
        cursor: 'pointer', minWidth: TAP_MIN, minHeight: TAP_MIN,
      }}>×</button>
    </div>
  )
}
